import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  LogIn, LogOut, Save, Upload, Image, FileText, Settings,
  Users, Inbox, ChevronRight, Check, X, Edit2, Eye, Plus
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("admin_token"));
  const [activeTab, setActiveTab] = useState("projects");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem("admin_token", res.data.token);
      setToken(res.data.token);
      setLoginError("");
    } catch (err) {
      setLoginError("Invalid credentials");
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
  };

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const loadData = async () => {
    try {
      const [p, s, r] = await Promise.all([
        axios.get(`${API}/admin/projects`, authHeaders()),
        axios.get(`${API}/admin/services`, authHeaders()),
        axios.get(`${API}/admin/service-requests`, authHeaders()),
      ]);
      setProjects(p.data);
      setServices(s.data);
      setRequests(r.data);
    } catch (err) {
      if (err.response?.status === 401) logout();
    }
  };

  const updateProject = async (id, data) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/projects/${id}`, data, authHeaders());
      setMessage("Project updated!");
      loadData();
      setEditingId(null);
    } catch (err) {
      setMessage("Error saving");
    }
    setSaving(false);
    setTimeout(() => setMessage(""), 3000);
  };

  const updateService = async (id, data) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/services/${id}`, data, authHeaders());
      setMessage("Service updated!");
      loadData();
      setEditingId(null);
    } catch (err) {
      setMessage("Error saving");
    }
    setSaving(false);
    setTimeout(() => setMessage(""), 3000);
  };

  const uploadImage = async (file, projectId) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/admin/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      await updateProject(projectId, { image: res.data.url });
    } catch (err) {
      setMessage("Upload failed");
    }
  };

  // Login Screen
  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A1F2E] flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 w-full max-w-md"
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#4CAF50] flex items-center justify-center rounded">
              <LogIn className="text-white" size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#0A1F2E]">Admin Login</h1>
              <p className="text-[#64748B] text-sm">ICC Bahamas CMS</p>
            </div>
          </div>

          <form onSubmit={login} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded"
                placeholder="admin"
                data-testid="admin-username"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded"
                placeholder="••••••••"
                data-testid="admin-password"
              />
            </div>
            {loginError && <p className="text-red-500 text-sm">{loginError}</p>}
            <button
              type="submit"
              data-testid="admin-login-btn"
              className="w-full bg-[#4CAF50] text-white p-3 rounded font-semibold hover:bg-[#43A047] transition"
            >
              Login
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#0A1F2E] text-white p-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="text-xl font-bold">ICC Bahamas <span className="text-[#4CAF50]">CMS</span></h1>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" className="flex items-center gap-1 text-sm text-white/70 hover:text-white">
              <Eye size={16} /> View Site
            </a>
            <button onClick={logout} className="flex items-center gap-2 text-sm bg-white/10 px-3 py-2 rounded hover:bg-white/20">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Message Toast */}
      {message && (
        <div className="fixed top-20 right-4 bg-[#4CAF50] text-white px-4 py-2 rounded shadow-lg z-50">
          {message}
        </div>
      )}

      <div className="max-w-7xl mx-auto p-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: "projects", label: "Projects", icon: Image },
            { id: "services", label: "Services", icon: Settings },
            { id: "images", label: "Site Images", icon: Upload },
            { id: "requests", label: "Inquiries", icon: Inbox },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded font-medium transition ${
                activeTab === tab.id
                  ? "bg-[#1E90DB] text-white"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Projects Tab */}
        {activeTab === "projects" && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-[#0A1F2E]">Manage Projects</h2>
            {projects.map((project) => (
              <div key={project.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex gap-6">
                  <div className="w-48 h-32 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                    <img src={project.image} alt={project.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    {editingId === `project-${project.id}` ? (
                      <ProjectEditForm
                        project={project}
                        onSave={(data) => updateProject(project.id, data)}
                        onCancel={() => setEditingId(null)}
                        onUpload={(file) => uploadImage(file, project.id)}
                        saving={saving}
                      />
                    ) : (
                      <>
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="text-xs text-[#4CAF50] font-semibold uppercase">{project.category}</span>
                            <h3 className="text-xl font-bold text-[#0A1F2E]">{project.name}</h3>
                          </div>
                          <button
                            onClick={() => setEditingId(`project-${project.id}`)}
                            className="flex items-center gap-1 text-[#1E90DB] text-sm hover:underline"
                          >
                            <Edit2 size={14} /> Edit
                          </button>
                        </div>
                        <p className="text-gray-600 mt-2 text-sm line-clamp-3">{project.description}</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Services Tab */}
        {activeTab === "services" && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-[#0A1F2E]">Manage Services</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {services.map((service) => (
                <div key={service.id} className="bg-white rounded-lg shadow p-6">
                  {editingId === `service-${service.id}` ? (
                    <ServiceEditForm
                      service={service}
                      onSave={(data) => updateService(service.id, data)}
                      onCancel={() => setEditingId(null)}
                      saving={saving}
                    />
                  ) : (
                    <>
                      <div className="flex justify-between items-start">
                        <h3 className="text-lg font-bold text-[#0A1F2E]">{service.title}</h3>
                        <button
                          onClick={() => setEditingId(`service-${service.id}`)}
                          className="flex items-center gap-1 text-[#1E90DB] text-sm hover:underline"
                        >
                          <Edit2 size={14} /> Edit
                        </button>
                      </div>
                      <p className="text-gray-600 mt-2 text-sm">{service.description}</p>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Site Images Tab */}
        {activeTab === "images" && <ImagesTab token={token} setMessage={setMessage} />}

        {/* Requests Tab */}
        {activeTab === "requests" && (
          <InquiriesTab
            requests={requests}
            onAdd={async (data) => {
              await axios.post(`${API}/admin/service-requests`, data, authHeaders());
              setMessage("Inquiry added!");
              loadData();
            }}
          />
        )}
      </div>
    </div>
  );
}

// Project Edit Form Component
function ProjectEditForm({ project, onSave, onCancel, onUpload, saving }) {
  const [name, setName] = useState(project.name);
  const [category, setCategory] = useState(project.category);
  const [description, setDescription] = useState(project.description);

  return (
    <div className="space-y-3">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="w-full p-2 border rounded font-bold"
        placeholder="Project Name"
      />
      <input
        type="text"
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        className="w-full p-2 border rounded text-sm"
        placeholder="Category"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        className="w-full p-2 border rounded text-sm"
        rows={4}
        placeholder="Description"
      />
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded cursor-pointer hover:bg-gray-200 text-sm">
          <Upload size={16} />
          Change Image
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])}
          />
        </label>
      </div>
      <div className="flex gap-2 pt-2">
        <button
          onClick={() => onSave({ name, category, description })}
          disabled={saving}
          className="flex items-center gap-1 px-4 py-2 bg-[#4CAF50] text-white rounded hover:bg-[#43A047]"
        >
          <Save size={16} /> {saving ? "Saving..." : "Save"}
        </button>
        <button
          onClick={onCancel}
          className="flex items-center gap-1 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
        >
          <X size={16} /> Cancel
        </button>
      </div>
    </div>
  );
}

// Service Edit Form Component
function ServiceEditForm({ service, onSave, onCancel, saving }) {
  const [title, setTitle] = useState(service.title);
  const [description, setDescription] = useState(service.description);

  return (
    <div className="space-y-3">
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full p-2 border rounded font-bold"
        placeholder="Service Title"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        className="w-full p-2 border rounded text-sm"
        rows={3}
        placeholder="Description"
      />
      <div className="flex gap-2">
        <button
          onClick={() => onSave({ title, description })}
          disabled={saving}
          className="flex items-center gap-1 px-4 py-2 bg-[#4CAF50] text-white rounded hover:bg-[#43A047]"
        >
          <Save size={16} /> {saving ? "Saving..." : "Save"}
        </button>
        <button
          onClick={onCancel}
          className="flex items-center gap-1 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
        >
          <X size={16} /> Cancel
        </button>
      </div>
    </div>
  );
}

export default AdminPage;

// ==================== SITE IMAGES TAB ====================
const IMAGE_SLOT_GROUPS = [
  {
    title: "Home Hero Slider",
    description: "Up to 4 rotating background images on the home page (1920×1080 landscape works best).",
    slots: [
      { key: "hero-1", label: "Hero slide 1 (leave empty for default)" },
      { key: "hero-2", label: "Hero slide 2" },
      { key: "hero-3", label: "Hero slide 3" },
      { key: "hero-4", label: "Hero slide 4 (data center)" },
    ],
  },
  {
    title: "Meet the Team",
    description: "Square headshots recommended (800×800).",
    slots: [
      { key: "team-timothy-walker", label: "Timothy Walker — President & Founder" },
      { key: "team-nikkea-jarrett", label: "Nikkea Jarrett — EA to the President" },
      { key: "team-lorenzo-flowers", label: "Lorenzo Flowers — Sales & Marketing Manager" },
    ],
  },
  {
    title: "Company Fleet Showcase",
    description: "Single featured photo on the Team page's fleet section (landscape works best).",
    slots: [
      { key: "fleet-showcase", label: "Fleet showcase photo" },
    ],
  },
  {
    title: "Photo Gallery",
    description: "Project / on-site photos for the public gallery page (up to 50).",
    slots: Array.from({ length: 50 }, (_, i) => ({
      key: `gallery-${i + 1}`,
      label: `Gallery photo ${i + 1}`,
    })),
  },
];

function ImagesTab({ token, setMessage }) {
  const [images, setImages] = useState({});
  const [uploadingKey, setUploadingKey] = useState(null);

  const loadImages = async () => {
    try {
      const res = await axios.get(`${API}/site-images`);
      setImages(res.data || {});
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    loadImages();
  }, []);

  const upload = async (slotKey, file) => {
    if (!file) return;
    setUploadingKey(slotKey);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/admin/site-images/${slotKey}`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setMessage("Uploaded!");
      await loadImages();
    } catch (e) {
      setMessage(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingKey(null);
      setTimeout(() => setMessage(""), 2500);
    }
  };

  const removeImage = async (slotKey) => {
    if (!window.confirm("Remove this image?")) return;
    try {
      await axios.delete(`${API}/admin/site-images/${slotKey}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage("Removed");
      await loadImages();
    } catch (e) {
      setMessage("Failed to remove");
    } finally {
      setTimeout(() => setMessage(""), 2500);
    }
  };

  return (
    <div className="space-y-10" data-testid="admin-images-tab">
      <div>
        <h2 className="text-2xl font-bold text-[#0A1F2E]">Site Images</h2>
        <p className="text-sm text-gray-600 mt-1">
          Drag &amp; drop a photo onto a slot, or click to choose a file. Images appear on the public site instantly.
        </p>
      </div>
      {IMAGE_SLOT_GROUPS.map((group) => (
        <div key={group.title}>
          <h3 className="text-lg font-bold text-[#0A1F2E]">{group.title}</h3>
          <p className="text-xs text-gray-500 mb-4">{group.description}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {group.slots.map((slot) => (
              <ImageSlot
                key={slot.key}
                slot={slot}
                imageUrl={images[slot.key]}
                uploading={uploadingKey === slot.key}
                onUpload={(file) => upload(slot.key, file)}
                onRemove={() => removeImage(slot.key)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ImageSlot({ slot, imageUrl, uploading, onUpload, onRemove }) {
  const [drag, setDrag] = useState(false);
  const inputId = `slot-input-${slot.key}`;

  const handleDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) onUpload(file);
  };

  return (
    <div
      data-testid={`image-slot-${slot.key}`}
      className={`bg-white rounded-lg border-2 border-dashed transition overflow-hidden ${
        drag ? "border-[#4CAF50] bg-green-50" : "border-gray-300"
      }`}
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={handleDrop}
    >
      <label htmlFor={inputId} className="block cursor-pointer">
        <div className="aspect-video bg-gray-100 flex items-center justify-center relative">
          {imageUrl ? (
            <img src={imageUrl} alt={slot.label} className="w-full h-full object-cover" />
          ) : (
            <div className="text-center text-gray-400 px-3">
              <Upload size={28} className="mx-auto mb-2" />
              <p className="text-xs">Drop image here<br />or click to upload</p>
            </div>
          )}
          {uploading && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-white text-xs">
              Uploading…
            </div>
          )}
        </div>
        <input
          id={inputId}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => onUpload(e.target.files?.[0])}
        />
      </label>
      <div className="p-3">
        <p className="text-xs font-semibold text-[#0A1F2E] truncate" title={slot.label}>
          {slot.label}
        </p>
        {imageUrl && (
          <button
            onClick={onRemove}
            data-testid={`remove-${slot.key}`}
            className="text-xs text-red-500 hover:underline mt-1"
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
}


// Inquiries tab — list of submissions + manual "Add Inquiry" form for staff.
function InquiriesTab({ requests, onAdd }) {
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    service_type: "", message: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setSaving(true);
    try {
      await onAdd(form);
      setForm({ name: "", email: "", phone: "", company: "", service_type: "", message: "" });
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#0A1F2E]">Service Inquiries</h2>
        <button
          data-testid="add-inquiry-btn"
          onClick={() => setShowForm((v) => !v)}
          className="inline-flex items-center gap-2 bg-[#4CAF50] text-white px-4 py-2 rounded-lg hover:bg-[#2E7D32] transition"
        >
          {showForm ? <X size={16} /> : <Plus size={16} />}
          {showForm ? "Cancel" : "Add Inquiry"}
        </button>
      </div>

      {showForm && (
        <form
          data-testid="inquiry-form"
          onSubmit={submit}
          className="bg-white rounded-lg shadow p-6 space-y-3"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              data-testid="inquiry-name"
              type="text"
              required
              placeholder="Name *"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1E90DB]"
            />
            <input
              data-testid="inquiry-email"
              type="email"
              required
              placeholder="Email *"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1E90DB]"
            />
            <input
              data-testid="inquiry-phone"
              type="text"
              placeholder="Phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1E90DB]"
            />
            <input
              data-testid="inquiry-company"
              type="text"
              placeholder="Company"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1E90DB]"
            />
            <input
              data-testid="inquiry-service-type"
              type="text"
              placeholder="Service Type (e.g. Fiber Optic Cabling)"
              value={form.service_type}
              onChange={(e) => setForm({ ...form, service_type: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 md:col-span-2 focus:outline-none focus:ring-2 focus:ring-[#1E90DB]"
            />
          </div>
          <textarea
            data-testid="inquiry-message"
            placeholder="Message / project details"
            rows={4}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1E90DB]"
          />
          <button
            data-testid="inquiry-submit-btn"
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#1E90DB] text-white px-5 py-2 rounded-lg hover:bg-[#0A4A7A] transition disabled:opacity-50"
          >
            <Save size={16} /> {saving ? "Saving..." : "Save Inquiry"}
          </button>
        </form>
      )}

      {requests.length === 0 ? (
        <p className="text-gray-500">No inquiries yet.</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-4 text-sm font-semibold text-gray-600">Name</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-600">Email</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-600">Phone</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-600">Company</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-600">Service</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-600">Date</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <tr key={req.id} className="border-t" data-testid={`inquiry-row-${req.id}`}>
                  <td className="p-4">{req.name}</td>
                  <td className="p-4 text-[#1E90DB]">{req.email}</td>
                  <td className="p-4">{req.phone || "—"}</td>
                  <td className="p-4">{req.company || "—"}</td>
                  <td className="p-4">{req.service_type || "—"}</td>
                  <td className="p-4 text-gray-500 text-sm">
                    {req.created_at ? new Date(req.created_at).toLocaleDateString() : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
